import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Trophy, 
  AlertCircle,
  TrendingUp,
  Calendar,
  UserCheck,
  ClipboardList,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Heart,
  Thermometer,
  Plane,
  Home,
  GraduationCap,
  Trash2,
  Bell,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Euro Icon Component
const EuroIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M15.999 8.5h2c0-2.837-2.755-4.131-5-4.429V2h-2v2.071c-2.245.298-5 1.592-5 4.429 0 2.706 2.666 4.113 5 4.43v4.97c-1.448-.251-3-1.024-3-2.4h-2c0 2.589 2.425 4.119 5 4.436V22h2v-2.07c2.245-.298 5-1.592 5-4.43s-2.755-4.131-5-4.429V6.1c1.33.239 3 .941 3 2.4z"/>
  </svg>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentTeams, setRecentTeams] = useState([]);
  const [recentPlayers, setRecentPlayers] = useState([]);
  const [recentTrainingSessions, setRecentTrainingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedTrainingSession, setSelectedTrainingSession] = useState(null);
  const [sessionPlayers, setSessionPlayers] = useState([]);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showTeamSelectionModal, setShowTeamSelectionModal] = useState(false);
  const [allTeams, setAllTeams] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    checkForNotification();
    
    // Set up automatic refresh every 5 minutes
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 5 * 60 * 1000); // 5 minutes
    
    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const checkForNotification = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      
      if (response.data.notification_enabled && response.data.notification_text) {
        const notificationId = response.data.notification_id;
        
        // Check if user has already seen this notification
        const seenNotifications = JSON.parse(localStorage.getItem('seenNotifications') || '[]');
        
        if (!seenNotifications.includes(notificationId)) {
          setNotification({
            id: notificationId,
            title: response.data.notification_title,
            text: response.data.notification_text
          });
          setShowNotification(true);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigung:', error);
    }
  };

  const handleCloseNotification = () => {
    // Mark notification as seen
    if (notification) {
      const seenNotifications = JSON.parse(localStorage.getItem('seenNotifications') || '[]');
      seenNotifications.push(notification.id);
      localStorage.setItem('seenNotifications', JSON.stringify(seenNotifications));
    }
    
    setShowNotification(false);
  };

  const handleCreateTraining = () => {
    setShowTeamSelectionModal(true);
  };

  const handleTeamSelect = (teamId) => {
    setShowTeamSelectionModal(false);
    navigate(`/teams/${teamId}/training`);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel for faster loading
      const [statsResponse, teamsResponse, playersResponse, trainingsResponse] = await Promise.all([
        axios.get(`${API}/stats/overview`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/players`),
        axios.get(`${API}/training-sessions?limit=5`)
      ]);
      
      setStats(statsResponse.data);
      
      // Store all teams for team selection modal
      setAllTeams(teamsResponse.data);
      
      // Sort teams by creation date (newest first) and take top 5
      const sortedTeams = teamsResponse.data
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      
      const players = playersResponse.data;
      
      // Add player count to each team
      const teamsWithPlayerCount = sortedTeams.map(team => {
        const playerCount = players.filter(player => player.team_id === team.id).length;
        return { ...team, player_count: playerCount };
      });
      
      setRecentTeams(teamsWithPlayerCount);
      
      // Sort players by creation date and get newest 5
      const sortedPlayers = players
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setRecentPlayers(sortedPlayers);

      // Process training sessions (already fetched in parallel)
      const allTrainingSessions = trainingsResponse.data;
      
      // Create a map for team name lookup from ALL teams (not just recent 5)
      const allTeams = teamsResponse.data;
      const allTeamMap = new Map(allTeams.map(t => [t.id, t.name]));
      
      // Enrich with team names (no filtering - show all trainings returned by backend)
      const enrichedSessions = allTrainingSessions
        .filter(session => session && session.team_id)
        .map(session => ({
          ...session,
          team_name: allTeamMap.get(session.team_id) || 'Unbekannt'
        }))
        .slice(0, 5);
      
      setRecentTrainingSessions(enrichedSessions);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Fehler beim Laden der Dashboard-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    toast.info('Dashboard wird aktualisiert...');
    await fetchDashboardData();
    toast.success('Dashboard erfolgreich aktualisiert!');
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'trainer':
        return 'Trainer';
      case 'leser':
        return 'Leser';
      default:
        return role;
    }
  };

  // Anwesenheitsstatus-Konfiguration (gleich wie in TrainingAttendance.js)
  const attendanceStatuses = [
    { value: 'present', label: 'Da', icon: CheckCircle, color: 'green' },
    { value: 'absent', label: 'Nicht da', icon: XCircle, color: 'red' },
    { value: 'injured', label: 'Verletzt', icon: Heart, color: 'orange' },
    { value: 'sick', label: 'Krank', icon: Thermometer, color: 'yellow' },
    { value: 'vacation', label: 'Urlaub', icon: Plane, color: 'blue' },
    { value: 'private', label: 'Privat', icon: Home, color: 'purple' },
    { value: 'ue', label: 'UE', icon: XCircle, color: 'red' }
  ];

  const handleTrainingSessionClick = async (session) => {
    try {
      setSelectedTrainingSession(session);
      
      // Fetch players for this team
      const playersResponse = await axios.get(`${API}/players?team_id=${session.team_id}`);
      setSessionPlayers(playersResponse.data);
      setShowAttendanceModal(true);
    } catch (error) {
      console.error('Error fetching session players:', error);
      toast.error('Fehler beim Laden der Spielerdaten');
    }
  };

  const getAttendanceStats = (session) => {
    const attendance = session.attendance || {};
    const stats = { total: sessionPlayers.length };
    
    attendanceStatuses.forEach(status => {
      stats[status.value] = Object.values(attendance).filter(s => s === status.value).length;
    });
    
    const recordedTotal = Object.values(stats).reduce((sum, count, index) => 
      index === 0 ? sum : sum + count, 0); // Skip 'total' in index 0
    stats.notSet = stats.total - recordedTotal;
    
    return stats;
  };

  const cleanupOrphanedTrainingSessions = async () => {
    if (!user || user.role !== 'admin') {
      toast.error('Nur Administratoren können verwaiste Trainingseinheiten löschen');
      return;
    }

    try {
      setCleaningUp(true);
      toast.info('Bereinige verwaiste Trainingseinheiten...');

      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/training-sessions/cleanup-orphaned`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.deleted_count > 0) {
        toast.success(`${response.data.deleted_count} verwaiste Trainingseinheiten wurden gelöscht`);
        
        // Refresh dashboard data to remove orphaned sessions from the list
        await fetchDashboardData();
      } else {
        toast.info('Keine verwaisten Trainingseinheiten gefunden');
      }
    } catch (error) {
      console.error('Error cleaning up orphaned training sessions:', error);
      toast.error('Fehler beim Bereinigen der verwaisten Trainingseinheiten');
    } finally {
      setCleaningUp(false);
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Willkommen zurück, {user?.username}!
            </h1>
            <p className="text-emerald-100">
              Sie sind als {getRoleDisplayName(user?.role)} angemeldet
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 bg-emerald-400 hover:bg-emerald-300 rounded-lg transition-colors disabled:opacity-50"
              title="Dashboard aktualisieren"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="hidden md:block">
              <Calendar className="w-16 h-16 text-emerald-200" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Neue Trainingseinheit - Shortcut */}
        <div 
          onClick={handleCreateTraining} 
          className="card hover:shadow-lg transition-shadow cursor-pointer group"
        >
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Neue Einheit</p>
                <p className="text-2xl font-bold text-emerald-600 group-hover:text-emerald-700">+ Erstellen</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <Calendar className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <Clock className="w-4 h-4 text-emerald-500 mr-1" />
              <span className="text-sm text-emerald-600">Trainingseinheit erstellen</span>
            </div>
          </div>
        </div>

        {/* Spieler */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Spieler</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.players_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <UserCheck className="w-4 h-4 text-emerald-500 mr-1" />
              <span className="text-sm text-emerald-600">Registrierte Spieler</span>
            </div>
          </div>
        </div>

        {/* Teams */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Teams</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.teams_count || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Trophy className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600">Aktive Teams</span>
            </div>
          </div>
        </div>

        {/* Beiträge (zusammengefasst) */}
        {user?.role !== 'leser' && (
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-gray-600">Beiträge</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <EuroIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              
              {/* Bezahlt */}
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <span className="text-sm text-gray-600">Bezahlt</span>
                </div>
                <span className="text-lg font-bold text-green-600">{stats?.paid_count || 0}</span>
              </div>
              
              {/* Ausstehend */}
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <span className="text-sm text-gray-600">Ausstehend</span>
                </div>
                <span className="text-lg font-bold text-red-600">{stats?.unpaid_count || 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Teams */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Aktuelle Teams</h3>
              <Trophy className="w-5 h-5 text-gray-400" />
            </div>
          </div>
          <div className="card-body">
            {recentTeams.length > 0 ? (
              <div className="space-y-3">
                {recentTeams.map((team) => (
                  <div key={team.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{team.name}</p>
                      <p className="text-sm text-gray-500">
                        {team.jahrgang && `Jahrgang ${team.jahrgang}`}
                        {team.liga && ` • ${team.liga}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {team.player_count} Spieler
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Keine Teams vorhanden</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Players - Hidden for Leser */}
        {user?.role !== 'leser' && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Neue Spieler</h3>
                <Users className="w-5 h-5 text-gray-400" />
              </div>
            </div>
            <div className="card-body">
              {recentPlayers.length > 0 ? (
                <div className="space-y-3">
                  {recentPlayers.map((player) => (
                    <div key={player.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {player.vorname.charAt(0)}{player.nachname.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {player.vorname} {player.nachname}
                          </p>
                          <p className="text-sm text-gray-500">
                            {calculateAge(player.geburtsdatum)} Jahre
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          player.mitgliedsbeitrag_bezahlt 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {player.mitgliedsbeitrag_bezahlt ? 'Bezahlt' : 'Offen'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Keine Spieler vorhanden</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Recent Training Sessions */}
      <div className="mt-8">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Letzte Trainingseinheiten</h3>
              <div className="flex items-center space-x-2">
                {user?.role === 'admin' && (
                  <button
                    onClick={cleanupOrphanedTrainingSessions}
                    disabled={cleaningUp}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Verwaiste Trainingseinheiten löschen"
                  >
                    <Trash2 className={`w-4 h-4 ${cleaningUp ? 'animate-pulse' : ''}`} />
                  </button>
                )}
                <Calendar className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div className="card-body">
            {recentTrainingSessions.length > 0 ? (
              <div className="space-y-3">
                {recentTrainingSessions
                  .filter(session => {
                    // Final UI-level filter: ensure we never show invalid team data
                    return session && 
                           session.team_name && 
                           session.team_name !== 'Unbekannt' && 
                           session.team_name !== 'Unbekanntes Team' &&
                           session.team_name.trim() !== '' &&
                           session.team_id;
                  })
                  .map((session) => {
                  const sessionDate = new Date(`${session.date} ${session.time}`);
                  const isToday = sessionDate.toDateString() === new Date().toDateString();
                  const isPast = sessionDate < new Date();
                  
                  return (
                    <div 
                      key={`${session.team_id}-${session.id}`} 
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => handleTrainingSessionClick(session)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isToday ? 'bg-emerald-100' : isPast ? 'bg-gray-200' : 'bg-blue-100'
                        }`}>
                          <Clock className={`w-6 h-6 ${
                            isToday ? 'text-emerald-600' : isPast ? 'text-gray-500' : 'text-blue-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {session.team_name && 
                             session.team_name !== 'Unbekannt' && 
                             session.team_name !== 'Unbekanntes Team' ? 
                             session.team_name : 'Kein gültiges Team'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(session.date).toLocaleDateString('de-DE')} • {session.time}
                          </p>
                          <p className="text-xs text-gray-400">
                            {session.duration} Minuten
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isPast ? 'bg-gray-100 text-gray-800' : isToday ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {isPast ? 'Vergangen' : isToday ? 'Heute' : 'Geplant'}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Klicken für Anwesenheit
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Keine Trainingseinheiten vorhanden</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Attendance Modal */}
      <Dialog open={showAttendanceModal} onOpenChange={setShowAttendanceModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Anwesenheit: {selectedTrainingSession?.team_name}
            </DialogTitle>
            <div className="text-sm text-gray-600">
              {selectedTrainingSession && (
                <>
                  {new Date(selectedTrainingSession.date).toLocaleDateString('de-DE')} • 
                  {selectedTrainingSession.time} • 
                  {selectedTrainingSession.duration} Minuten
                </>
              )}
            </div>
          </DialogHeader>

          {selectedTrainingSession && sessionPlayers.length > 0 && (
            <div className="mt-6">
              {/* Statistics Overview */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Anwesenheitsübersicht</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {attendanceStatuses.map(status => {
                    const stats = getAttendanceStats(selectedTrainingSession);
                    const count = stats[status.value] || 0;
                    if (count === 0) return null;
                    
                    const StatusIcon = status.icon;
                    return (
                      <div key={status.value} className="flex items-center space-x-2">
                        <StatusIcon className={`w-4 h-4 text-${status.color}-500`} />
                        <span className="text-sm">
                          <span className="font-medium">{count}</span> {status.label}
                        </span>
                      </div>
                    );
                  })}
                  {(() => {
                    const stats = getAttendanceStats(selectedTrainingSession);
                    return stats.notSet > 0 ? (
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">
                          <span className="font-medium">{stats.notSet}</span> Nicht erfasst
                        </span>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Player List */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Spieler-Details</h4>
                {sessionPlayers.map((player) => {
                  const playerStatus = selectedTrainingSession.attendance?.[player.id] || 'not_set';
                  const statusConfig = attendanceStatuses.find(s => s.value === playerStatus);
                  
                  return (
                    <div key={player.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {player.vorname.charAt(0)}{player.nachname.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {player.vorname} {player.nachname}
                          </p>
                          <p className="text-sm text-gray-500">
                            {player.trikotnummer && `#${player.trikotnummer}`}
                            {player.position && player.position.length > 0 && ` • ${player.position.join(', ')}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {statusConfig ? (
                          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${
                            statusConfig.color === 'green' ? 'bg-green-100 text-green-800' :
                            statusConfig.color === 'red' ? 'bg-red-100 text-red-800' :
                            statusConfig.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                            statusConfig.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            statusConfig.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                            statusConfig.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                            statusConfig.color === 'indigo' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            <statusConfig.icon className="w-4 h-4" />
                            <span>{statusConfig.label}</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                            <Users className="w-4 h-4" />
                            <span>Nicht erfasst</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Notes */}
              {selectedTrainingSession.notes && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Notizen</h4>
                  <p className="text-sm text-blue-800">{selectedTrainingSession.notes}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end mt-6">
            <Button 
              variant="outline" 
              onClick={() => setShowAttendanceModal(false)}
            >
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Benachrichtigung Dialog */}
      <Dialog open={showNotification} onOpenChange={setShowNotification}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <Bell className="w-5 h-5 text-blue-600" />
                </div>
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  {notification?.title || 'Benachrichtigung'}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {notification?.text}
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleCloseNotification}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Verstanden
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Selection Modal for Creating Training */}
      <Dialog open={showTeamSelectionModal} onOpenChange={setShowTeamSelectionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Team auswählen
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Für welches Team möchten Sie eine Trainingseinheit erstellen?
            </p>
            
            <div className="space-y-2">
              {allTeams.length > 0 ? (
                allTeams.map((team) => (
                  <button
                    key={team.id}
                    onClick={() => handleTeamSelect(team.id)}
                    className="w-full p-4 text-left border border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 group-hover:text-emerald-700">
                          {team.name}
                        </p>
                        {(team.jahrgang || team.liga) && (
                          <p className="text-sm text-gray-500 mt-1">
                            {team.jahrgang && `Jahrgang ${team.jahrgang}`}
                            {team.liga && team.jahrgang && ' • '}
                            {team.liga}
                          </p>
                        )}
                      </div>
                      <Trophy className="w-5 h-5 text-gray-400 group-hover:text-emerald-500" />
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Trophy className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Keine Teams vorhanden</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => setShowTeamSelectionModal(false)}
            >
              Abbrechen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;